import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import LinkIcon from '../icons/LinkIcon';
import PinIcon from '../icons/PinIcon';
import PDFIcon from '../icons/PDFIcon';
import CompareIcon from '../icons/CompareIcon';
import SaveIcon from '../icons/SaveIcon';
import AlertIcon from '../icons/AlertIcon';
import RemoveIcon from '../icons/RemoveIcon';
import FlagIcon from '../icons/FlagIcon';
import On3LogoIcon from '../icons/On3LogoIcon';
import Score, { SCORE_TYPE } from '../atoms/LabledScore';
import Loader from '../atoms/Loader';
import Avatar from '../atoms/Avatar';
import NamedBox from '../atoms/NamedBox';
import Tooltip from '../atoms/Tooltip';
import { ToastType } from '../atoms/Toast';
import CloseButton from '../atoms/CloseButton';
import EditButton from '../atoms/EditButton';
import DropDown, { DropDownItem } from '../atoms/DropDown';
import DashboardIcon from '../icons/DashboardIcon';
import PFFIcon from '../icons/PffLinkIcon';
import TFVLogoIcon from '../icons/TFVLogoIcon';
import TeamAutoComplete from '../molecules/TeamAutoComplete';
import Dialog from '../molecules/Dialog';
import MultiSportList from '../molecules/MultiSportList';
import ConfirmDialog from './ConfirmDialog';
import { PlayerPDFQueryParameter, TEAM_TYPE } from '../templates/PlayerPageTemplate';
import gql from '../services/gql';
import { getPrimaryHighSchool } from '../services/player';
import { formatValueInK } from '../services/user';
import { fetchUserPlayerValidationSetting } from '../redux/dispatchers/user-settings';
import exportPDFReport, { PDFExportPage } from '../services/export-pdf-report';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_LIGHT,
  COLOR_TEXT,
  COLOR_DARK_GRAY,
  COLOR_ORANGE,
  COLOR_BACKGROUND_WARM,
  COLOR_MIDDLE_GRAY,
  COLOR_WHITE,
  COLOR_BLUE,
  COLOR_SHADOW,
  COLOR_GREEN,
} from '../styles/colors';
import Player, { playerNarrative, PlayerSeasonGrade } from '../types/Player';
import Team from '../types/Team';
import User from '../types/User';
import Position from '../types/Position';
import SavedPlayer from '../types/SavedPlayer';
import { PlayerEvaluationDataResponse } from '../types/Evaluation';
import LabledScore from '../atoms/LabledScore';
import Toggle from '../atoms/Toggle';
import IconButton from '../atoms/IconButton';
import { RemoveCircleOutlineOutlined } from '@material-ui/icons';
import { State } from '../redux/reducers';
import NotAvailable from '../atoms/NotAvailable';
import { useQuery } from '@tanstack/react-query';
import { formatNumber } from '../services/converter';
import { Valuations } from '../types/PlayerValuationProfileFactors';
import PlayerBoardDialog from './PlayerBoardDialog';
import HighSchool from '../types/HighSchool';
export interface UpdatedPlayerProfileHeaderProps {
  player?: Player;
  loading: boolean;
  divITeams: Team[];
  nflTeams: Team[];
  selectedTeamType: string;
  setSelectedTeamType: React.Dispatch<React.SetStateAction<string>>;
  selectedCollegeTeam?: Team;
  selectedNFLTeam?: Team;
  positions?: Position[];
  selectedPosition?: Position;
  setSelectedPosition: React.Dispatch<React.SetStateAction<Position | undefined>>;
  user?: User;
  onCollegeTeamSelected: (team: Team | null) => void;
  onNFLTeamSelected: (team: Team | null) => void;
  setLoading: (isLoading: boolean) => void;
  showToast: (message: any, type: ToastType) => void;
  onEditPlayer: () => void,
  isNFL?: boolean;
  isAdmin?: boolean;
  IS_ADMIN_OR_NFL_SCOUT?: boolean;
  evaluationTeam?: Team | null;
  playerEvaluationData?: PlayerEvaluationDataResponse | null;
  onScoreNavigate?: (scoreType: string) => void;
  playerSeasonGrade?: PlayerSeasonGrade;
  hsTFValuations?: Valuations | null;
  collegeTFValuations?: Valuations | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    background: COLOR_BACKGROUND_LIGHT,
    minHeight: 240,
    padding: theme.spacing(5, 5, 3),

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(5, 3, 3, 3),
    },

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 0),
      background: COLOR_WHITE,

      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: 80,
        background: COLOR_BACKGROUND_LIGHT,
      },
    },

    [theme.breakpoints.down('xs')]: {
      minHeight: 'unset',
      padding: theme.spacing(2, 1, 0),
    },
  },
  headerContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(5),
    position: 'relative',
    zIndex: 1,

    '@media (max-width: 1680px)': {
      gap: theme.spacing(3),
    },

    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  playerIntro: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: 200,

    [theme.breakpoints.down('md')]: {
      width: 160,
    },

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },

  mobileIntroRow: {
    display: 'none',

    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) 120px minmax(0, 1fr)',
      alignItems: 'start',
      columnGap: theme.spacing(.5),
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 2,
    },

    '@media (max-width: 450px)': {
      gridTemplateColumns: 'minmax(0, 1fr) 100px minmax(0, 1fr)',
    },
  },
  mobileTeamTypeSelectWrap: {
    flex: '0 0 auto',
    minWidth: 0,
    justifySelf: 'start',

    '@media (max-width: 450px)': {
      width: '100%',
    },
  },
  mobileTeamTypeSelect: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    minWidth: 0,
    maxWidth: 120,
    borderRadius: 999,
    border: `2px solid ${COLOR_MIDDLE_GRAY}`,

    '& .MuiOutlinedInput-root': {
      border: 0,
    },

    '@media (max-width: 450px)': {
      width: '100%',
    },
  },
  mobileDropdownSelectorRoot: {
    borderRadius: 0,
    boxShadow: 'none',
    boxSizing: 'border-box',
    minHeight: 42,
    paddingRight: theme.spacing(5),
    fontSize: theme.typography.pxToRem(14),
    
    '@media (max-width: 450px)': {
      fontSize: theme.typography.pxToRem(13),
    },
  },
  mobileDropdownFieldsetClass: {
    '& fieldset': {
      boxShadow: 'none',
    },
  },
  mobileAvatarContainer: {
    position: 'relative',
    width: 'fit-content',
    flex: '0 0 auto',
    justifySelf: 'center',
  },
  mobileAvatar: {
    width: 120,
    height: 120,
    margin: '0 auto',
    border: `1px solid ${COLOR_MIDDLE_GRAY}`,

    '@media (max-width: 450px)': {
      width: 100,
      height: 100,
    },
  },
  mobileTeamSelectTriggerWrapper: {
    display: 'none',

    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'flex-end',
      flex: '0 0 auto',
      justifySelf: 'end',
      minWidth: 0,
      position: 'relative',
      zIndex: 2,

      '& .team-select-mobile-button': {
        minWidth: 0,
        padding: theme.spacing(1, 2),
      },
    },

    '@media (max-width: 450px)': {
      width: '100%',

      '& .team-select-mobile-button': {
        width: '100%',
        maxWidth: 120,
        padding: theme.spacing(1),
        fontSize: theme.typography.pxToRem(13),
      },
    },
  },
  mobileTeamSelectTrigger: {
    minWidth: 0,
    padding: theme.spacing(1, 1.5),
    position: 'relative',
    zIndex: 2,

    '@media (max-width: 450px)': {
      padding: theme.spacing(1, 1.25),
    },
  },

  avatarContainer: {
    position: 'relative',
    width: 'fit-content',
    margin: '0 auto',

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  avatar: {
    width: 128,
    height: 128,
    margin: '0 auto',
    border: `1px solid ${COLOR_MIDDLE_GRAY}`,

    [theme.breakpoints.down('xs')]: {
      width: 100,
      height: 100,
    },
  },
  playerIntroContent: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    textAlign: 'center',
  },
  editAction: {
    position: 'absolute',
    color: COLOR_BLUE,
    bottom: '0px',
    right: '-4px',
    background: COLOR_WHITE,
    zIndex: 10,
    borderRadius: '50%',
    height: 40,
    width: 40,
    boxShadow: `0 10px 10px 0 ${COLOR_SHADOW}`,
    border: `1px solid ${COLOR_SHADOW}`,
    cursor: 'pointer',
  },
  playerName: {
    margin: theme.spacing(1, 0, 0, 0),
    fontSize: theme.typography.pxToRem(28),
    lineHeight: 1.1,
    color: COLOR_TEXT,

    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.pxToRem(24),
    },

    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(22),
    },
  },
  playerOverview: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
    },
  },
  playerOverviewRow: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',

    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'center',
    },

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  scoreAndMultiSportRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(3, 10),
    marginRight: 'auto',

    '@media (max-width: 1680px)': {
      gap: theme.spacing(3),
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      width: '100%',
      marginRight: 0,
    },
  },
  playerScoreRow: {
    display: 'flex',
    gap: theme.spacing(3),

    '@media (max-width: 1680px)': {
      gap: theme.spacing(1.5),
    },

    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'center',
    },

    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
    },
  },
  playerScoreWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 150,

    '&:first-of-type': {
      marginLeft: 0,
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      gap: theme.spacing(1),
    },
  },
  freshmanDataFlag: {
    position: 'absolute',
    top: theme.spacing(-1),
    left: '50%',
    transform: 'translate(-50%, -100%)',

    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      transform: 'none',
    },
  },
  freshmanIcon: {
    cursor: 'help',
    height: 18,
    width: 18,
  },
  paiFreshmanIcon: {
    color: COLOR_ORANGE,
  },
  combineFreshmanIcon: {
    color: COLOR_GREEN,
  },
  playerScore: {
  },
  playerMultiSColumn: {
    display: 'flex',

    [theme.breakpoints.down('sm')]: {
      flexGrow: 1,
      flexWrap: 'wrap',
      gap: theme.spacing(2, 0),
      justifyContent: 'center',
      margin: `0 ${theme.spacing(-2)}px`,
      padding: theme.spacing(3, 2),
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },

    [theme.breakpoints.down('xs')]: {
      margin: `0 ${theme.spacing(-1)}px`,
    },
  },
  playerMultiSSection: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(.5),
    minHeight: 60,
    minWidth: 96,
    padding: theme.spacing(0, 4),
    borderRight: `2px solid ${COLOR_MIDDLE_GRAY}`,

    '&:first-child': {
      paddingLeft: 0,
    },

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },

    '@media (max-width: 1680px)': {
      padding: theme.spacing(0, 1.5),
    },
  },
  multiSportSpecialistChip: {
    backgroundColor: COLOR_DARK_GRAY,
    borderRadius: '999px',
    color: COLOR_WHITE,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    lineHeight: 1,
    padding: theme.spacing(1, 2.5),
    marginBottom: theme.spacing(1),
  },
  multiSportCount: {
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: COLOR_TEXT,
    whiteSpace: 'nowrap',
    lineHeight: 1.1,
  },
  evalSportCount: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    color: COLOR_TEXT,
    whiteSpace: 'nowrap',
    lineHeight: 1.1,
    paddingRight: theme.spacing(1),
  },
  evalColorSwatch: {
    width: 55,
    height: 24,
    borderRadius: 5,
    boxShadow: '0 0 0 5px #dbdbdb',
    boxSizing: 'content-box' as const,
  },
  multiSportLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: 26,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  multiSportIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    minWidth: 44,
    height: 26,

    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  multiSportTooltipList: {
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  multiSportTooltipIcon: {
    margin: 0,
    color: COLOR_WHITE,
  },
  pdLeft: {
    paddingLeft: '18px'
  },
  noScoreText: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
  },
  checkIconClass: {
    height: 26,
    width: 26,

    [theme.breakpoints.down('sm')]: {
      height: 22,
      width: 22,
    },
  },
  proCombineWrapper: {
  },
  teamSelectTriggerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignSelf: 'center',
    marginLeft: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  teamSelectTrigger: {
    appearance: 'none',
    background: COLOR_BACKGROUND_WARM,
    border: `2px solid ${COLOR_MIDDLE_GRAY}`,
    borderRadius: 999,
    boxSizing: 'border-box',
    color: COLOR_TEXT,
    cursor: 'pointer',
    minHeight: 46,
    minWidth: 140,
    padding: theme.spacing(1, 3),
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    lineHeight: 1,
    textAlign: 'center',
    boxShadow: 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',

    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: `0 10px 10px 0 ${COLOR_SHADOW}`,
      borderColor: '#cfcfcf',
    },

    '&:focus': {
      outline: 0,
      boxShadow: `0 0 0 3px ${COLOR_BLUE}33`,
    },

    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
    },
  },
  teamSelectModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 0,
  },
  teamSelectModalPaper: {
    width: '100%',
    maxWidth: 360,
    boxShadow: theme.shadows[10],
    outline: 'none',
    borderRadius: 8,
    overflow: 'hidden',
  },
  closeButton: {
    marginLeft: 'auto',
  },
  teamSelectModalContent: {
    background: COLOR_WHITE,
    padding: theme.spacing(6, 3, 6),
  },
  teamSelectModalBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  teamSelectRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  teamSelectDropdown: {
    width: 250,

    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 320,
    },
  },
  teamSelect: {
    width: 140,

    '& input': {
      boxSizing: 'border-box',
      height: 50,
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
      maxWidth: 320,
    },
  },
  withNFLTeamSelect: {
    width: 224,
    minWidth: 224,
  },
  switch: {
    flexShrink: 0,
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(5),

    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
      gap: theme.spacing(2),
    },

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),

    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center',
    },
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 50,
    height: 50,
    padding: theme.spacing(1),
    borderRadius: '50%',
    border: `2px solid ${COLOR_MIDDLE_GRAY}`,
    color: COLOR_DARK_GRAY,
    background: COLOR_BACKGROUND_WARM,
    fontSize: theme.typography.pxToRem(16),
    transition: 'background 0.3s',

    '&:hover': {
      background: COLOR_BACKGROUND_LIGHT,
      textDecoration: 'none',
      borderColor: COLOR_BLUE,

      '& $actionIcon': {
        color: COLOR_BLUE,
      },

      '& $actionText': {
        color: COLOR_TEXT,
      },
    },

    [theme.breakpoints.down('sm')]: {
      width: 44,
      height: 44,
    },
  },
  actionIcon: {
    width: 22,
    height: 22,
  },
  actionText: {
    color: COLOR_TEXT,
  },
  aiAction: {
    '&:hover': {
      borderColor: COLOR_ORANGE,

      '& $aiActionText': {
        color: COLOR_ORANGE,
      },
    },
  },
  aiActionText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  aiModalPaper: {
    width: '100%',
    maxWidth: 740,
    boxShadow: theme.shadows[10],
    outline: 'none',
    borderRadius: 8,
    overflow: 'hidden',
  },
  aiModalContent: {
    padding: theme.spacing(2.5, 3, 3),
  },
  aiOverviewText: {
    margin: 0,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 1.6,
    color: COLOR_TEXT,
    whiteSpace: 'pre-line',
  },
  boardAction: {
    '&:hover': {
      borderColor: COLOR_GREEN,

      '& $boardActionText': {
        color: COLOR_GREEN,
      },
    },
  },
  boardActionText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    color: COLOR_TEXT,
  },
  notesContainer: {},
  topPlayers: {},
  aiOverviewShimmer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  aiOverviewShimmerLine: {
    position: 'relative',
    height: 14,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',

    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      transform: 'translateX(-100%)',
      background: 'linear-gradient(90deg, rgba(240,240,240,0) 0%, rgba(255,255,255,0.9) 50%, rgba(240,240,240,0) 100%)',
      animation: '$aiOverviewShimmerMove 1.2s ease-in-out infinite',
    },
  },
  aiOverviewShimmerLineLong: {
    width: '100%',
  },
  aiOverviewShimmerLineMedium: {
    width: '92%',
  },
  aiOverviewShimmerLineShort: {
    width: '70%',
  },
  '@keyframes aiOverviewShimmerMove': {
    '100%': {
      transform: 'translateX(100%)',
    },
  },
}), { name: UpdatedPlayerProfileHeader.name });

export default function UpdatedPlayerProfileHeader(props: UpdatedPlayerProfileHeaderProps) {
  const {
    player,
    loading,
    divITeams,
    nflTeams,
    selectedTeamType,
    setSelectedTeamType,
    selectedCollegeTeam,
    selectedNFLTeam,
    positions,
    selectedPosition,
    setSelectedPosition,
    user,
    onCollegeTeamSelected,
    onNFLTeamSelected,
    setLoading,
    showToast,
    onEditPlayer,
    isNFL,
    isAdmin,
    IS_ADMIN_OR_NFL_SCOUT,
    evaluationTeam,
    playerEvaluationData,
    onScoreNavigate,
    playerSeasonGrade,
    hsTFValuations,
    collegeTFValuations
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isArchived, setIsArchived] = useState<boolean>(Boolean(player?.isArchived));
  const [isPinned, setIsPinned] = useState<boolean>(false);
  const [savedPlayer, setSavedPlayer] = useState<SavedPlayer | undefined>(undefined);
  const [hasAlert, setHasAlert] = useState<boolean>(false);
  const [teamSelectModalOpen, setTeamSelectModalOpen] = useState<boolean>(false);
  const [aiModalOpen, setAiModalOpen] = useState<boolean>(false);
  const [boardModalOpen, setBoardModalOpen] = useState<boolean>(false);
  const {
    data: narrativeData,
    isFetching: narrativeLoading,
    refetch: refetchNarrative,
  } = useQuery<playerNarrative | null>(
    ['getPlayerNarrative', player?.id],
    () => {
      if (!player?.id) {
        return Promise.resolve(null);
      }

      return gql(`
          getPlayerNarrative (playerId: ${player.id}) {
            narrative
          }
        `)
        .then((data: any) => data.getPlayerNarrative as playerNarrative);
    },
    {
      enabled: false,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );
  const isSkipChecked = useSelector((state: State) => {
    if (!player?.id) return undefined;
    return state.userSettings.playerValidationSettingByPlayerId[player.id];
  });

  const playerEvaluationValidationIsAll = useSelector((state: State) => {
    if (!player?.id) return undefined;
    return state.userSettings.playerEvaluationValidationIsAll;
  });

  const [archivePlayerDialogOpen, setArchivePlayerDialogOpen] = useState<boolean>(false);
  const [removePlayerDialogOpen, setRemovePlayerDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (player) {
      setLoading(true);

      if (player.isArchived) {
        setIsArchived(true);
      }

      Promise.allSettled([
        checkIsPinned(player.id),
        checkIsSavedPlayer(player.id),
        checkPlayerHasAlert(player.id),
      ])
        .finally(() => setLoading(false));
    }
  }, [player]);

  useEffect(() => {
    if (!player?.id) {
      return;
    }

    if (typeof isSkipChecked === 'boolean') {
      return;
    }

    Promise.resolve((dispatch as any)(fetchUserPlayerValidationSetting(player.id)))
      .catch(console.error);
  }, [dispatch, player?.id, isSkipChecked]);

  const playerHasColleges = player && player.playerColleges && player.playerColleges.length > 0;
  const playerHasNFLTeams = player && player.playerNFLTeams && player.playerNFLTeams.length > 0;
  const playerHasHighSchools = player && player.highSchools && player.highSchools.length > 0;
  const playerIsHS = playerHasHighSchools && !playerHasColleges && !playerHasNFLTeams;
  const canTeamSelectCollege = !!user?.accessLevel?.canTeamSelectCollege;
  const canTeamSelectNFL = !!user?.accessLevel?.canTeamSelectNFL;
  const canShowTeamSelect = canTeamSelectCollege || canTeamSelectNFL;
  const canShowBoardAction = !!user?.accessLevel?.recruitingEvaluationBoard;
  const valuationScore = selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? hsTFValuations?.playerValuationScore : collegeTFValuations?.playerValuationScore;
  const formattedValuationScore = !!valuationScore && typeof valuationScore === 'number'
    ? valuationScore.toFixed(1)
    : 'N/A';

  const primaryHS:HighSchool = (player?.highSchools || []).find((highSchool:HighSchool) => highSchool && highSchool.isPrimary) || {} as HighSchool;
  const isAiEnable = primaryHS?.graduatingClass > 2009;

  function checkIsPinned(id: number) {
    return gql(`
        isPinned (playerId: ${id})
      `)
      .then((data: any) => data && data.isPinned as boolean)
      .then((pinnedStatus: boolean) => setIsPinned(pinnedStatus));
  }

  function checkIsSavedPlayer(id: number) {
    return gql(`
        getSavedPlayer (playerId: ${id}) {
          id
          playerId
          folderId
          player {
            id
            slug
            firstName
            lastName
            photoUrl
            pai
            combine
            highSchools
          }
          userId
          createdAt
          updatedAt
        }
      `)
      .then((data: any) => data.getSavedPlayer)
      .then((data: SavedPlayer) => setSavedPlayer(data))
      .catch(console.error);
  }

  function checkPlayerHasAlert(id: number) {
    return gql(`
        hasAlert (playerId: ${id})
      `)
      .then((data: any) => data.hasAlert as boolean)
      .then((hasAlert: boolean) => setHasAlert(hasAlert))
      .catch(console.error)
  }

  function onToggleArchivePlayer() {
    setArchivePlayerDialogOpen(false);

    if (!player || !player.slug) return;

    setLoading(true);

    gql(`
        mutation {
          toggleArchive(slug: "${player.slug}")
        }
      `)
      .then((data: any) => data.toggleArchive as boolean)
      .then((success: boolean) => {
        if (success) {
          showToast(player.isArchived ? 'Unarchived' : 'Archived', ToastType.SUCCESS);

          setIsArchived(!isArchived);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function onRemovePlayer() {
    setRemovePlayerDialogOpen(false);

    if (!player || !player.slug) return;

    setLoading(true);

    gql(`
        mutation {
          deletePlayer(slug: "${player.slug}")
        }
      `)
      .then((data: any) => data.deletePlayer as boolean)
      .then((success: boolean) => {
        if (success) {
          showToast('Removed', ToastType.SUCCESS);

          return new Promise<void>((resolve) => {
            setTimeout(() => {
              history.replace('/dashboard');
              resolve();
            }, 2000);
          });
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function pinPlayer() {
    setLoading(true);

    gql(`
        mutation {
          pin (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => ({
        success: data.pin as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }: { success: boolean, errorMessage: string }) => {
        setIsPinned(success);

        if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function unpinPlayer() {
    setLoading(true);

    gql(`
        mutation {
          unpin (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => data && data.unpin as boolean)
      .then((success: boolean) => setIsPinned(!success))
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function savePlayer() {
    setLoading(true);

    gql(`
        mutation {
          savePlayers (playerIds: [${player && player.id}], folderId: null)
        }
      `)
      .then((data: any) => data.savePlayers as boolean)
      .then((isSaved: boolean) => {
        if (isSaved) {
          player && player.id && checkIsSavedPlayer(player.id)
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function unsavePlayer() {
    setLoading(true);

    gql(`
        mutation {
          unsavePlayers (savedPlayerIds: [${savedPlayer && savedPlayer.id}])
        }
      `)
      .then((data: any) => data.unsavePlayers as boolean)
      .then((isSaved: boolean) => {
        if (isSaved) {
          player && player.id && checkIsSavedPlayer(player.id)
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function addAlert() {
    setLoading(true);

    gql(`
        mutation {
          addAlerts (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => data.addAlerts as boolean)
      .then((alertStatus: boolean) => setHasAlert(alertStatus))
      .catch((error) => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function removeAlert() {
    setLoading(true);

    gql(`
        mutation {
          removeAlerts (playerIds: [${player && player.id}])
        }
      `)
      .then((data: any) => data.removeAlerts as boolean)
      .then((alertRemoved: boolean) => setHasAlert(!alertRemoved))
      .catch((error) => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function generateAndDownloadPDFReport() {
    if (!player?.slug) return;

    setLoading(true);

    let exportQueryParameters: any = {
      [PlayerPDFQueryParameter.PROFILE_TYPE]: isNFL ? 'nfl' : 'college',
    };
    if (selectedCollegeTeam?.id) {
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_COLLEGE_TEAM_ID] = selectedCollegeTeam.id;
    }
    if (isNFL && selectedNFLTeam?.id) {
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_NFL_TEAM_ID] = selectedNFLTeam.id;
    }
    if (selectedPosition) {
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_POSITION] = selectedPosition;
    }
    if (selectedTeamType) {
      let selectedTeamForQueryParameters = 'high-school';
      if (selectedTeamType === TEAM_TYPE.COLLEGE) {
        selectedTeamForQueryParameters = 'college';
      } else if (selectedTeamType === TEAM_TYPE.NFL) {
        selectedTeamForQueryParameters = 'nfl';
      }
      exportQueryParameters[PlayerPDFQueryParameter.SELECTED_TEAM_TYPE] = selectedTeamForQueryParameters;
    }

    exportPDFReport(
      PDFExportPage.PLAYER,
      player.slug,
      exportQueryParameters,
    )
      .catch(error => {
        console.error(error);
        showToast(<>Failed to download PDF report. <br />({error.message})</>, ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(window.location.href);
    showToast('Copied to clipboard', ToastType.INFO);
  }

  function openBoardModal() {
    setBoardModalOpen(true);
  }

  function onOpenAiOverview() {
    if (!player?.id) {
      return;
    }

    if (!narrativeData?.narrative && !narrativeLoading) {
      refetchNarrative().catch(console.error);
    }

    setAiModalOpen(true);
  }

  const primaryHighSchool = getPrimaryHighSchool(player);
  const playerMultiSport = player?.multiSport?.length ?? 0
  const evaluationColor = playerEvaluationData?.customFields?.evalColor?.value;
  const showEvaluationOnHeader = Boolean(player?.showEvaluationOnHeader);
  const shouldShowEvaluationValues = Boolean(
    playerEvaluationValidationIsAll === true
    && isSkipChecked === false
    && (playerEvaluationData?.customFields?.evalScore?.value || playerEvaluationData?.customFields?.evalCode?.value || evaluationColor )
  );

  const getSeasonGrade = (seasonGrade: PlayerSeasonGrade | undefined) => {
    if (!seasonGrade) return null;
    if (!seasonGrade?.defense && !seasonGrade?.offense) return null;
    if (seasonGrade?.defense && seasonGrade?.offense) {
      return Math.max(seasonGrade.defense, seasonGrade.offense).toFixed(1);
    }

    return (seasonGrade.defense || seasonGrade.offense)?.toFixed(1);
  }

  return (
    <>
      <header className={classes.header}>
        <Loader inProgress={loading} />

        <div className={classes.headerContent}>
          {player && (
            <>
              <div className={classes.playerIntro}>
                <div className={classes.mobileIntroRow}>
                  <div className={classes.mobileTeamTypeSelectWrap}>
                    {player?.lastPublishedDate ? (
                      <DropDown
                        className={classes.mobileTeamTypeSelect}
                        selectorRootClassName={classes.mobileDropdownSelectorRoot}
                        items={[
                          (playerHasHighSchools || playerHasColleges || playerHasNFLTeams) && { content: 'High School', value: TEAM_TYPE.HIGH_SCHOOL },
                          (playerHasColleges || playerHasNFLTeams) && { content: 'College', value: TEAM_TYPE.COLLEGE },
                          playerHasNFLTeams && { content: 'NFL', value: TEAM_TYPE.NFL }
                        ].filter(Boolean) as DropDownItem[]}
                        value={selectedTeamType}
                        onChange={(value: string) => setSelectedTeamType(value)}
                        fieldsetClassName={classes.mobileDropdownFieldsetClass}
                      />
                    ) : null}
                  </div>

                  <div className={classes.mobileAvatarContainer}>
                    <Avatar
                      className={classes.mobileAvatar}
                      src={player.photoUrl}
                      initials={`${(player.firstName || ' ')[0]}${(player.lastName || ' ')[0]}`.trim()}
                      alt='Profile picture'
                    />
                    {isAdmin && (
                      <EditButton
                        className={classes.editAction}
                        onClick={onEditPlayer}
                      />
                    )}
                  </div>

                  {canShowTeamSelect && (
                    <div className={classes.mobileTeamSelectTriggerWrapper}>
                      <button
                        type='button'
                        className={`${classes.teamSelectTrigger} team-select-mobile-button ${classes.mobileTeamSelectTrigger}`}
                        onClick={() => setTeamSelectModalOpen(true)}
                      >
                        TeamSelect
                      </button>
                    </div>
                  )}
                </div>

                <div className={classes.avatarContainer}>
                  <Avatar
                    className={classes.avatar}
                    src={player.photoUrl}
                    initials={`${(player.firstName || ' ')[0]}${(player.lastName || ' ')[0]}`.trim()}
                    alt='Profile picture'
                  />
                  {isAdmin && (
                    <EditButton
                      className={classes.editAction}
                      onClick={onEditPlayer}
                    />
                  )}
                </div>

                <div className={classes.playerIntroContent}>
                  <h1 className={classes.playerName}>
                    {player.firstName} {player.lastName}
                  </h1>
                </div>
              </div>
              
              <div className={classes.playerOverview}>
                <div className={classes.playerOverviewRow}>
                  <div className={classes.scoreAndMultiSportRow}>
                    <div className={classes.playerScoreRow}>
                      <div className={classes.playerScoreWrapper}>
                        {
                          player?.pai && player?.pai.length > 0 ? (
                            <LabledScore
                              className={classes.playerScore}
                              noScoreTextClass={classes.noScoreText}
                              type={SCORE_TYPE.PAI}
                              scoreList={player.pai}
                              position={selectedPosition}
                              label='PAI'
                              onClick={() => onScoreNavigate?.(SCORE_TYPE.PAI)}
                            />
                          )
                          : (
                            <LabledScore
                              className={classes.playerScore}
                              noScoreTextClass={classes.noScoreText}
                              type={SCORE_TYPE.PPAI}
                              scoreList={player.projectedPAI}
                              position={selectedPosition}
                              label='PPAI'
                              onClick={() => onScoreNavigate?.(SCORE_TYPE.PAI)}
                            />
                          )
                        }

                        {!!player?.paiHasFreshmanData && (
                          <div className={classes.freshmanDataFlag}>
                            <Tooltip title='Freshman Data'>
                              <FlagIcon className={clsx(classes.freshmanIcon, classes.paiFreshmanIcon)} />
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      {
                        selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && 
                        <div className={classes.playerScoreWrapper}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.COMBINE}
                            scoreList={player.combine}
                            position={selectedPosition}
                            combines={player.hsCombines}
                            label='COM'
                            onClick={() => onScoreNavigate?.(SCORE_TYPE.COMBINE)}
                          />

                          {!!player?.combineHasFreshmanData && (
                            <div className={classes.freshmanDataFlag}>
                              <Tooltip title='Freshman Data'>
                                <FlagIcon className={clsx(classes.freshmanIcon, classes.combineFreshmanIcon)} />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      }

                      {selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && player?.bestHighSchoolMPH?.mph && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                        <div className={clsx(classes.playerScoreWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.MPH}
                            value={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                            totalValue={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                            label='MPH'
                            onClick={() => onScoreNavigate?.(SCORE_TYPE.MPH)}
                            isHSMPH={selectedTeamType === TEAM_TYPE.HIGH_SCHOOL}
                          />
                        </div>
                      )}

                      {
                        (selectedTeamType === TEAM_TYPE.COLLEGE || selectedTeamType === TEAM_TYPE.NFL)
                        // player?.ppi && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) 
                      && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PPI}
                            value={player.ppi || undefined}
                            position={selectedPosition}
                            label='PPI'
                            onClick={() => onScoreNavigate?.(SCORE_TYPE.PPI)}
                          />
                        </div>
                      )}

                      {
                        (selectedTeamType === TEAM_TYPE.COLLEGE || selectedTeamType === TEAM_TYPE.NFL) 
                        //&& IS_ADMIN_OR_NFL_SCOUT
                        //&& selectedTeamType === TEAM_TYPE.COLLEGE
                        //&& player?.bestCollegeMPH?.mph 
                          ? <div className={classes.playerScoreWrapper}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            value={player?.bestCollegeMPH?.mph}
                            type={SCORE_TYPE.MPH}
                            totalValue={player?.bestCollegeMPH?.mph || 0}
                            label='MPH'
                            onClick={() => onScoreNavigate?.(SCORE_TYPE.MPH)}
                          />
                        </div> : ''
                      }

                      {
                        selectedTeamType === TEAM_TYPE.NFL
                      // (player?.nflCombineScore || !!player?.nflCombines?.length) 
                      && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PRO_COMBINE}
                            value={player.nflCombineScore || undefined}
                            position={selectedPosition}
                            combines={player.nflCombines}
                            label='Pro'
                            onClick={() => onScoreNavigate?.(SCORE_TYPE.PRO_COMBINE)}
                          />
                        </div>
                      )}

                      {/* {selectedTeamType === TEAM_TYPE.HIGH_SCHOOL && player?.bestHighSchoolMPH?.mph && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                        <div className={clsx(classes.playerScoreWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.MPH}
                            value={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                            totalValue={(selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? player?.bestHighSchoolMPH?.mph : player?.bestCollegeMPH?.mph) || undefined}
                            label='MPH'
                          />
                        </div>
                      )}

                      {player?.ppi && (user?.accessLevel?.hasCollegeAccess || user?.accessLevel?.hasNFLAccess) && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            noScoreTextClass={classes.noScoreText}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PPI}
                            value={player.ppi || undefined}
                            position={selectedPosition}
                            label='PPI'
                          />
                        </div>
                      )}

                      {(player?.nflCombineScore || !!player?.nflCombines?.length) && (
                        <div className={clsx(classes.playerScoreWrapper, classes.proCombineWrapper)}>
                          <LabledScore
                            className={classes.playerScore}
                            checkIconClass={classes.checkIconClass}
                            type={SCORE_TYPE.PRO_COMBINE}
                            value={player.nflCombineScore || undefined}
                            position={selectedPosition}
                            combines={player.nflCombines}
                            label='Pro Com'
                          />
                        </div>
                      )} */}
                    </div>
                    {/* <div>

                    </div> */}
                    {/* Grade */}
                    {shouldShowEvaluationValues ?
                    <div className={classes.playerMultiSColumn}>
                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                        {playerEvaluationData?.customFields?.evalScore?.value || 'N/A'}
                        </span>

                        <div className={classes.multiSportLabel}>
                          <span className={classes.evalSportCount}>
                            Grade
                          </span>
                          <img
                            src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                            alt={evaluationTeam?.name || 'Team Logo'}
                            width={20}
                            height={20}
                          />

                        </div>
                      </div>

                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                          {playerEvaluationData?.customFields?.evalCode?.value || 'N/A'}
                        </span>

                        <div className={classes.multiSportLabel}>
                          <span className={classes.evalSportCount}>
                            Code
                          </span>
                          <img
                            src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                            alt={evaluationTeam?.name || 'Team Logo'}
                            width={20}
                            height={20}
                          />
                        </div>
                      </div>

                      <div className={classes.playerMultiSSection}>
                        {
                           !evaluationColor ? 
                            <span className={classes.multiSportCount}>
                              N/A
                            </span>
                            :
                            <div
                              className={classes.evalColorSwatch}
                              style={{ backgroundColor: evaluationColor }}
                              title={evaluationColor}
                            />
                        }

                        <div className={classes.multiSportLabel}>
                          <span className={classes.evalSportCount}>
                            Color
                          </span>
                          <img
                            src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                            alt={evaluationTeam?.name || 'Team Logo'}
                            width={20}
                            height={20}
                          />
                        </div>
                      </div>
                    </div>
                    :
                      // Multi-sport and TFV/On3 valuations
                    <div className={classes.playerMultiSColumn}>
                      {
                        selectedTeamType !== TEAM_TYPE.HIGH_SCHOOL ?
                          <div className={classes.playerMultiSSection}>
                            <span className={classes.multiSportCount}>
                              {getSeasonGrade(playerSeasonGrade) || "N/A"}
                            </span>

                            <div className={classes.multiSportLabel}>
                              <PFFIcon className={classes.multiSportIcon} />
                            </div>
                          </div>
                        : 
                          <div className={classes.playerMultiSSection}>
                            <span className={classes.multiSportCount}>
                              <Tooltip
                                title={playerMultiSport > 0 ? (
                                  <MultiSportList
                                    className={classes.multiSportTooltipList}
                                    iconClassName={classes.multiSportTooltipIcon}
                                    list={player?.multiSport}
                                  />
                                ) : 'Specialist' }
                              >
                                <div>{playerMultiSport}</div>
                              </Tooltip>
                            </span>

                            <div className={classes.multiSportLabel}>
                              HS Sports
                            </div>
                          </div>
                      }


                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                          {/* {hsTFValuations?.playerValuation ? `${formatNumber(Number(hsTFValuations?.playerValuation || 0), 0)}` : 'N/A'} */}
                          {formattedValuationScore || "N/A"}
                        </span>

                        <div className={classes.multiSportLabel}>
                          <TFVLogoIcon className={classes.multiSportIcon} />
                      
                        </div>
                      </div>

                      <div className={classes.playerMultiSSection}>
                        <span className={classes.multiSportCount}>
                          {player?.on3PublicNILValuation ? `$${formatValueInK(player?.on3PublicNILValuation)}` : 'N/A'}
                        </span>

                        <div className={classes.multiSportLabel}>
                          <On3LogoIcon className={classes.multiSportIcon} />
                        </div>
                      </div>
                    </div>
                    }
                  </div>

                  {canShowTeamSelect && (
                    <div className={classes.teamSelectTriggerWrapper}>
                      <button
                        type='button'
                        className={classes.teamSelectTrigger}
                        onClick={() => setTeamSelectModalOpen(true)}
                      >
                        TeamSelect
                      </button>
                    </div>
                  )}
                </div>

                <div className={classes.playerOverviewRow}>
                  <div className={classes.toolbarRow}>
                    <Toggle
                      className={classes.switch}
                      items={(positions ?? []).map((position: Position) => ({
                        value: position,
                        content: position,
                      }))}
                      value={String(selectedPosition)}
                      onSelect={(value: string) => setSelectedPosition(value as Position)}
                    />

                    <div className={classes.actions}>
                      {isAdmin && (
                        <Tooltip title='Archive Player'>
                          <div>
                            <IconButton
                              className={classes.action}
                              icon={RemoveIcon}
                              iconClassName={classes.actionIcon}
                              onClick={() => setArchivePlayerDialogOpen(true)}
                            />
                          </div>
                        </Tooltip>
                      )}

                      {isAdmin && (
                        <Tooltip title='Remove Player'>
                          <div>
                            <IconButton
                              className={classes.action}
                              icon={RemoveCircleOutlineOutlined}
                              iconClassName={classes.actionIcon}
                              onClick={() => setRemovePlayerDialogOpen(true)}
                            />
                          </div>
                        </Tooltip>
                      )}
                      
                      <Tooltip title='Copy Link'>
                        <div>
                          <IconButton
                            className={classes.action}
                            icon={LinkIcon}
                            iconClassName={classes.actionIcon}
                            onClick={copyToClipboard}
                          />
                        </div>
                      </Tooltip>

                      <Tooltip title={isPinned ? 'Unpin Player' : 'Pin Player'}>
                        <div>
                          <IconButton
                            className={classes.action}
                            icon={PinIcon}
                            iconClassName={classes.actionIcon}
                            onClick={isPinned ? unpinPlayer : pinPlayer}
                          />
                        </div>
                      </Tooltip>

                      <Tooltip title='Download Report'>
                        <div>
                          <IconButton
                            className={classes.action}
                            icon={PDFIcon}
                            iconClassName={classes.actionIcon}
                            onClick={generateAndDownloadPDFReport}
                            disabled={loading}
                          />
                        </div>
                      </Tooltip>

                      {user?.accessLevel?.canComparePlayers && (
                        <Tooltip title='Compare'>
                          <div>
                            <IconButton
                              className={classes.action}
                              icon={CompareIcon}
                              iconClassName={classes.actionIcon}
                              href={player && player.slug ? `/compare/${player.slug}` : '/compare'}
                            />
                          </div>
                        </Tooltip>
                      )}

                      <Tooltip title={savedPlayer !== null ? 'Unsave Player' : 'Save Player'}>
                        <div>
                          <IconButton
                            className={classes.action}
                            icon={SaveIcon}
                            iconClassName={classes.actionIcon}
                            onClick={savedPlayer !== null ? unsavePlayer : savePlayer}
                          />
                        </div>
                      </Tooltip>

                      {!isNFL && !(playerHasColleges || playerHasNFLTeams) && (
                        <Tooltip title={hasAlert ? 'Remove Alert' : 'Add Alert'}>
                          <div>
                            <IconButton
                              className={classes.action}
                              icon={AlertIcon}
                              iconClassName={classes.actionIcon}
                              onClick={hasAlert ? removeAlert : addAlert}
                            />
                          </div>
                        </Tooltip>
                      )}

                      {
                        player?.id && isAiEnable && (
                          <Tooltip title='AI Overview'>
                            <div>
                              <button
                                type='button'
                                className={clsx(classes.action, classes.aiAction)}
                                onClick={onOpenAiOverview}
                              >
                                <span className={classes.aiActionText}>AI</span>
                              </button>
                            </div>
                          </Tooltip>
                        )
                      }

                       {canShowBoardAction && (
                        <Tooltip title='Add Player To Board'>
                          <div>
                            <button
                              type='button'
                              className={clsx(classes.action, classes.boardAction)}
                              onClick={openBoardModal}
                            >
                              <DashboardIcon className={classes.actionIcon} />
                              <span className={classes.boardActionText}>Board</span>
                            </button>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>
      </header>
      
      <ConfirmDialog
        open={archivePlayerDialogOpen}
        confirmActionText={`Yes, ${isArchived ? 'Unarchive' : 'Archive'}`}
        onConfirm={onToggleArchivePlayer}
        onClose={() => setArchivePlayerDialogOpen(false)}
        onCancel={() => setArchivePlayerDialogOpen(false)}
      />

      <ConfirmDialog
        open={removePlayerDialogOpen}
        confirmActionText={`Yes, Remove`}
        onConfirm={onRemovePlayer}
        onClose={() => setRemovePlayerDialogOpen(false)}
        onCancel={() => setRemovePlayerDialogOpen(false)}
      />

      <Dialog
        className={classes.teamSelectModal}
        contentClassName={classes.teamSelectModalPaper}
        open={teamSelectModalOpen}
        title='TeamSelect'
        noHeader={true}
        noContentPadding={true}
        onClose={() => setTeamSelectModalOpen(false)}
      >
        <NamedBox
          headerContent={<CloseButton className={classes.closeButton} onClick={() => setTeamSelectModalOpen(false)} />}
          name='TeamSelect'
        >
          <div className={classes.teamSelectModalContent}>
            <div className={classes.teamSelectModalBody}>
              {canTeamSelectCollege && (
                <div className={classes.teamSelectRow}>
                  <TeamAutoComplete
                    className={classes.teamSelectDropdown}
                    teams={divITeams}
                    selectedTeam={selectedCollegeTeam}
                    onSelect={onCollegeTeamSelected}
                    selectedCategory='college'
                    limit={divITeams.length || 5}
                  />
                </div>
              )}

              {(isNFL && canTeamSelectNFL) && (
                <div className={classes.teamSelectRow}>
                  <TeamAutoComplete
                    className={classes.teamSelectDropdown}
                    teams={nflTeams}
                    selectedTeam={selectedNFLTeam}
                    onSelect={onNFLTeamSelected}
                    selectedCategory='nfl'
                    limit={nflTeams.length || 5}
                  />
                </div>
              )}
            </div>
          </div>
        </NamedBox>
      </Dialog>

      <PlayerBoardDialog
        open={boardModalOpen}
        onClose={() => setBoardModalOpen(false)}
        playerId={player?.id}
        programId={user?.program?.id}
        showToast={showToast}
      />

      <Dialog
        className={classes.teamSelectModal}
        contentClassName={classes.aiModalPaper}
        open={aiModalOpen}
        title='AI Overview'
        noHeader={true}
        noContentPadding={true}
        onClose={() => setAiModalOpen(false)}
      >
        <NamedBox
          headerContent={<CloseButton className={classes.closeButton} onClick={() => setAiModalOpen(false)} />}
          name='AI Overview'
        >
          <div className={classes.aiModalContent}>
            {narrativeLoading ? (
              <div className={classes.aiOverviewShimmer} aria-label='Loading narrative'>
                <span className={clsx(classes.aiOverviewShimmerLine, classes.aiOverviewShimmerLineLong)} />
                <span className={clsx(classes.aiOverviewShimmerLine, classes.aiOverviewShimmerLineLong)} />
                <span className={clsx(classes.aiOverviewShimmerLine, classes.aiOverviewShimmerLineMedium)} />
                <span className={clsx(classes.aiOverviewShimmerLine, classes.aiOverviewShimmerLineShort)} />
              </div>
            ) : (
              <p className={classes.aiOverviewText}>{narrativeData?.narrative || <NotAvailable />}</p>
            )}
          </div>
        </NamedBox>
      </Dialog>
    </>
  )
}
